/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */

import * as Yup from 'yup';

import { Button, Spinner } from 'react-bootstrap';
import { Form as FormikForm, Formik } from 'formik';
import { useState, useContext } from 'react';
import { saveExpense, updateExpense } from '../../utils/API/expenses';
import { UserContext } from '../../context/UserContext';
import { employmentAwareDateRange } from '../../utils/Utility';
import DropFileZone from '../../components/Form/DropFileZone';
import {
  canHaveCompanyInvoice,
  DATE_NEW_EXPENSES_TYPE,
  EXPENSES_TYPES,
  EXPENSES_TYPES_OLD,
} from '../../constants';
import FormCheckbox from '../../components/Form/FormCheckbox';
import FormItem from '../../components/Form/FormItem';
import FormSelect from '../../components/Form/FormSelect';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import FormDatePicker from '../../components/Form/FormDatePicker';
import moment from 'moment';

const initialValues = (currentData) => ({
  id: currentData.id || undefined,
  date: currentData.date ? moment(currentData.date) : moment(),
  description: currentData.description || '',
  type: currentData.type || '',
  cost: currentData.cost || '',
  invoice_made_out_to_company: currentData.invoice_made_out_to_company || false,
  is_guild: currentData.is_guild || false,
  attachment: (() => {
    if (!get(currentData, 'attachment', null)) {
      return '';
    }
    const fileData = { name: get(currentData, 'attachment', null) };
    return { fileData, deleted: false };
  })(),
  attachment_receipt: (() => {
    if (!get(currentData, 'attachment_receipt', null)) {
      return '';
    }
    const fileData = { name: get(currentData, 'attachment_receipt', null) };
    return { fileData, deleted: false };
  })(),
});

const validationSchema = Yup.object().shape({
  type: Yup.string().required('Campo obbligatorio'),
  description: Yup.string()
    .required('Campo obbligatorio')
    .min(5, 'Lunghezza minima 5 caratteri')
    .max(50, 'Lunghezza massima 50 caratteri'),
  cost: Yup.number()
    .required('Campo obbligatorio')
    .positive("Valore non ammesso, dev'essere maggiore di 0"),
  date: Yup.date().required('Campo obbligatorio'),
  attachment: Yup.mixed().required('Campo obbligatorio'),
  attachment_receipt: Yup.mixed().required('Campo obbligatorio'),
});

export const ExpenseForm = ({
  close,
  currentData,
  month,
  updateData,
  isReadOnly,
}) => {
  const { user } = useContext(UserContext);
  const { minDate, maxDate } = employmentAwareDateRange(month, user.incoming_date, user.outgoing_date);
  const [
    executeRemoveSelectedFilesAttachment,
    setExecuteRemoveSelectedFilesAttachment,
  ] = useState(false);
  const [isFileAttachment, setIsFileAttachment] = useState(false);
  const attachments = [];
  const [
    executeRemoveSelectedFilesReceipt,
    setExecuteRemoveSelectedFilesReceipt,
  ] = useState(false);
  const [isFileReceiptAttachment, setIsFileReceiptAttachment] = useState(false);
  const receiptAttachments = [];

  const dateSelected = new Date(
    month.format('YYYY'),
    month.format('MM') - 1,
    1
  );

  const isNew = !currentData?.id;

  if (currentData && currentData.attachment) {
    const fileData = { name: get(currentData, 'attachment', null) };
    attachments.push({ fileData, deleted: false });
  }

  if (currentData && currentData.attachment_receipt) {
    const fileData = { name: get(currentData, 'attachment_receipt', null) };
    receiptAttachments.push({ fileData, deleted: false });
  }

  const { t } = useTranslation();

  const createOptions = () => {
    const html = [];
    const expensesTypes =
      DATE_NEW_EXPENSES_TYPE > dateSelected
        ? EXPENSES_TYPES_OLD
        : EXPENSES_TYPES;
    expensesTypes.forEach((item) => {
      const disabled =
        (currentData.id &&
          currentData.type !== 'training_expenses' &&
          item === 'training_expenses') ||
        (currentData.id &&
          currentData.type !== 'training_budget' &&
          item === 'training_budget');
      html.push(
        <option disabled={disabled} value={disabled ? '' : item} key={item}>
          {t(item)}
        </option>
      );
    });
    return html;
  };

  async function onSubmit(values, { setSubmitting, resetForm, setFieldValue }) {
    const {
      id,
      createAnother,
      type,
      invoice_made_out_to_company: companyInvoice,
    } = values;
    const cleanedValues = {
      ...values,
      invoice_made_out_to_company:
        companyInvoice && canHaveCompanyInvoice(type),
      date: values.date.format('YYYY-MM-DD'),
    };
    try {
      if (id) {
        await updateExpense(cleanedValues);
      } else {
        await saveExpense(cleanedValues);
      }
    } finally {
      setSubmitting(false);
      if (!createAnother) {
        updateData();
        close();
      } else {
        updateData();
        resetForm();
        setExecuteRemoveSelectedFilesAttachment(true);
        setExecuteRemoveSelectedFilesReceipt(true);
        setFieldValue('createAnother', createAnother);
      }
    }
  }

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={async (values, actions) => {
        await onSubmit(values, actions, close, updateData);
      }}
      initialValues={initialValues(currentData)}
    >
      {({ values, handleSubmit, isSubmitting, setFieldValue }) => {
        const enableInvoiceCheckbox = canHaveCompanyInvoice(values.type);
        const buttonText = currentData.id ? 'Aggiorna' : 'Inserisci';

        return (
          <FormikForm noValidate onSubmit={handleSubmit}>
            <FormDatePicker
              name="date"
              label="Data Spesa *"
              minDate={minDate}
              maxDate={maxDate}
              disabled={isReadOnly}
            />
            <FormSelect
              name="type"
              label="Tipologia spesa *"
              options={createOptions()}
              disabled={
                currentData.type === 'training_expenses' ||
                currentData.type === 'training_budget' ||
                isReadOnly
              }
            />
            <FormItem
              type="textarea"
              name="description"
              rows="1"
              label="Descrizione *"
              disabled={isReadOnly}
            />
            <FormItem
              type="number"
              pattern="[0-9]+"
              name="cost"
              label="Spesa (€) *"
              disabled={isReadOnly}
            />
            <DropFileZone
              name="attachment"
              fileTypes=".pdf, .png, .jpeg, .jpg"
              text="trascina la fattura o lo scontrino oppure fai click per selezionare il file da allegare come fattura o scontrino alla nota spese"
              setFieldValue={setFieldValue}
              attachments={attachments}
              executeRemoveSelectedFiles={executeRemoveSelectedFilesAttachment}
              setExecuteRemoveSelectedFiles={
                setExecuteRemoveSelectedFilesAttachment
              }
              setIsFileAttachment={setIsFileAttachment}
              labelName="Fattura o scontrino*"
              disabled={isReadOnly}
            />
            <DropFileZone
              name="attachment_receipt"
              fileTypes=".pdf, .png, .jpeg, .jpg"
              text="trascina il pagamento elettronico oppure fai click per selezionare il file da allegare come pagamento elettronico alla nota spese"
              setFieldValue={setFieldValue}
              attachments={receiptAttachments}
              executeRemoveSelectedFiles={executeRemoveSelectedFilesReceipt}
              setExecuteRemoveSelectedFiles={
                setExecuteRemoveSelectedFilesReceipt
              }
              setIsFileAttachment={setIsFileReceiptAttachment}
              labelName="Pagamento elettronico*"
              disabled={isReadOnly}
            />
            <div className="inline-fields">
              <FormCheckbox
                name="invoice_made_out_to_company"
                label="Fattura intestata all'azienda"
                disabled={!enableInvoiceCheckbox || isReadOnly}
              />
              <FormCheckbox
                name="is_guild"
                label="Spesa per gilda"
                disabled={isReadOnly}
              />
            </div>
            <div className="buttonContent">
              {isNew && (
                <>
                  <hr />
                  <FormCheckbox
                    name="createAnother"
                    label="Crea subito un'altra spesa"
                    disabled={isReadOnly}
                  />
                </>
              )}
              <div className="spacer" />
              <Button
                variant="secondary"
                onClick={close}
                disabled={isSubmitting}
              >
                {isReadOnly ? 'Chiudi' : 'Annulla'}
              </Button>
              {isReadOnly ? null : (
                <Button
                  variant="primary"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !isFileAttachment ||
                    !isFileReceiptAttachment
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        style={{ marginRight: '8px' }}
                      />
                      {buttonText}
                    </>
                  ) : (
                    buttonText
                  )}
                </Button>
              )}
            </div>
          </FormikForm>
        );
      }}
    </Formik>
  );
};
